import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

const STEPS = [
  { id: 'profile', label: 'Set up your profile', path: '/profile' },
  { id: 'household', label: 'Name your household', path: '/settings/household' },
  { id: 'invite', label: 'Invite a family member', path: '/members' },
  { id: 'calendar', label: 'Add an event to the calendar', path: '/calendar' },
  { id: 'shopping', label: 'Create a shopping list', path: '/shopping' },
  { id: 'tasks', label: 'Add your first task', path: '/tasks' },
]

export { STEPS }

export const useOnboardingProgress = () => {
  const { user, household } = useAuth()
  const qc = useQueryClient()
  const key = ['onboarding', user?.id, household?.id]

  const query = useQuery({
    queryKey: key,
    enabled: !!user?.id && !!household?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('onboarding_progress')
        .select('id, completed_steps, dismissed')
        .eq('user_id', user.id)
        .eq('household_id', household.id)
        .maybeSingle()
      if (error) throw error
      return data ?? { completed_steps: [], dismissed: false }
    },
  })

  const markStep = useMutation({
    mutationFn: async (stepId) => {
      const current = query.data?.completed_steps ?? []
      if (current.includes(stepId)) return
      const updated = [...current, stepId]
      const { error } = await supabase
        .from('onboarding_progress')
        .upsert(
          { user_id: user.id, household_id: household.id, completed_steps: updated },
          { onConflict: 'user_id,household_id' }
        )
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const dismiss = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('onboarding_progress')
        .upsert(
          { user_id: user.id, household_id: household.id, dismissed: true },
          { onConflict: 'user_id,household_id' }
        )
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const completedSteps = query.data?.completed_steps ?? []
  const isDismissed = query.data?.dismissed ?? false
  const progress = Math.round((completedSteps.length / STEPS.length) * 100)
  const isComplete = completedSteps.length >= STEPS.length

  return { ...query, markStep, dismiss, completedSteps, isDismissed, progress, isComplete, steps: STEPS }
}
